import { faBell, faBellSlash } from '@fortawesome/pro-regular-svg-icons';

import { useTranslate } from '@growtherapy/i18n';
import {
  Body,
  Button,
  Display,
  DrawerBody,
  DrawerFooter,
  Eyebrow,
  Icon,
  Subhead,
  cn,
  useContentMask,
} from '@growtherapy/sprout-ui';

import {
  SessionIntentionNotificationCadence,
  SessionIntentionNotificationTimeOfDay,
} from '@grow-therapy-team/seedling-components';

import type { SessionIntention } from '../../hooks/intentions/types';
import type { CurrentSessionIntention } from '../../hooks/intentions/useGetCurrentIntentionQuery';

// If the intention length exceeds this threshold, it will be displayed in a smaller font size.
const TEXT_DOWNSIZE_THRESHOLD = 45;

export type CompletedIntentionDrawerBodyProps = {
  isDarkTheme: boolean;
  onClickEdit: () => void;
  onClickDone: () => void;
  intention: SessionIntention | CurrentSessionIntention;
};

export function CompletedIntentionDrawerBody({
  isDarkTheme,
  onClickEdit,
  onClickDone,
  intention,
}: CompletedIntentionDrawerBodyProps) {
  const t = useTranslate();
  const { maskClassName, maskProps } = useContentMask({ mask: 'exclude' });

  const cadence = (() => {
    switch (intention.notificationCadence) {
      case SessionIntentionNotificationCadence.DAILY:
        return t('telehealth-core:intentions.day');
      case SessionIntentionNotificationCadence.EVERY_OTHER_DAY:
        return t('telehealth-core:intentions.otherDay');
      case SessionIntentionNotificationCadence.WEEKLY:
        return t('telehealth-core:intentions.week');
      default:
        return '';
    }
  })();

  const timeOfDay = (() => {
    switch (intention.notificationTimeOfDay) {
      case SessionIntentionNotificationTimeOfDay.MORNING:
        return t('telehealth-core:intentions.aMorning');
      case SessionIntentionNotificationTimeOfDay.AFTERNOON:
        return t('telehealth-core:intentions.anAfternoon');
      case SessionIntentionNotificationTimeOfDay.EVENING:
        return t('telehealth-core:intentions.anEvening');
      default:
        return '';
    }
  })();

  const textLength = intention.contentText?.length ?? 0;
  const IntentionTextComponent = textLength <= TEXT_DOWNSIZE_THRESHOLD ? Display.Small : Subhead.Default;

  return (
    <>
      <DrawerBody className='rebrand:sm:grow-0 p-350 my-auto justify-center items-center text-center'>
        <div className='sm:max-w-[430px] flex flex-col gap-500 mx-auto items-center'>
          <div
            className='p-550 rounded-475-500 w-full bg-cover bg-center items-center text-center my-250 flex flex-col gap-350'
            style={{ backgroundImage: `url('${intention.contentImageUrl}')` }}
          >
            <Eyebrow.Large className='uppercase rebrand:text-text-inverse'>
              {t('telehealth-core:intentions.yourIntention')}
            </Eyebrow.Large>
            <IntentionTextComponent
              className={cn(maskClassName, 'rebrand:text-text-inverse break-words min-w-0 max-w-full')}
              {...maskProps}
            >
              &ldquo;{intention.contentText}&rdquo;
            </IntentionTextComponent>
            {cadence && timeOfDay ? (
              <Body.Small as='span' className='rebrand:text-text-inverse'>
                <Icon icon={faBell} className='mr-100 text-text-inverse' />
                {t('telehealth-core:intentions.timeOfDayReminderEveryCadence', {
                  timeOfDay,
                  cadence,
                })}
              </Body.Small>
            ) : (
              <Body.Small as='span' className='rebrand:text-text-inverse'>
                <Icon icon={faBellSlash} className='mr-100 text-text-inverse' />
                {t('telehealth-core:intentions.noRemindersSet')}
              </Body.Small>
            )}
          </div>
          <div className='flex flex-col gap-250 mt-auto text-center'>
            <Subhead.Small className={cn('font-medium', { 'rebrand:text-text-inverse': isDarkTheme })}>
              {t('telehealth-core:intentions.intentionsWorkBestWhenYouRevisitThem')}
            </Subhead.Small>
            <Body.Default className={cn({ 'rebrand:text-text-inverse': isDarkTheme })}>
              {t('telehealth-core:intentions.youllGetRemindersOnTheGrowAppToCheckIn')}
            </Body.Default>
          </div>
        </div>
      </DrawerBody>
      <DrawerFooter className='justify-end rebrand:gap-100'>
        <Button onClick={onClickEdit} use='secondary'>
          {t('telehealth-core:core.edit')}
        </Button>
        <Button onClick={onClickDone}>{t('telehealth-core:core.done')}</Button>
      </DrawerFooter>
    </>
  );
}
